import React, { useMemo, useState } from 'react';
import { useTheme, Box, Tooltip } from '@mui/material';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from 'recharts';
import { useTranslation } from "react-i18next";
import { Query } from 'system/query/Query';

import { CategoryWidgetCategory, CategoryWidgetData, MakeCategorySegmentsFromSingleResponse } from 'components/widgets/utility/CategoryChartsAPIResponseHandlers';
import WidgetAlert, { WidgetErrorType } from 'components/widgetParts/WidgetAlert';
import RadarChartTooltip from './RadarChartTooltip';
import WidgetTitle from 'components/widgetParts/WidgetTitle';
import WidgetLayout from 'components/widgetParts/WidgetLayout';
import { DashboardQueryDrilldownDialog } from 'components/dialogs/QueryDrilldown/DashboardQueryDrilldownDialog';
import WidgetLegend from 'components/widgetParts/WidgetLegend';
import { radarChartDefaultSettings } from './RadarChartUtils';
import { WidgetLegendSettingsDefaults } from 'components/settingsConfig/uiComponents/SettingsLegend';
import withQueryWidget, { WithQueryWidgetProps } from 'components/widgetParts/queryWidgetComposition/withQueryWidget';
import { getSingleOriginalRequestByName, getSingleWidgetResponseByName } from 'system/widget/Widget';

function RadarChartContent(props: WithQueryWidgetProps): React.JSX.Element {
  const theme = useTheme();
  const { i18n } = useTranslation();

  const [isHoveringLabel, setIsHoveringLabel] = useState<boolean>(false);
  const [isHoveringChart, setIsHoveringChart] = useState<boolean>(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [selectedValue, setSelectedValue] = useState<any>(null);

  const apiResponseInfo = getSingleWidgetResponseByName(props.apiResponses, 'radar-chart');
  const originalRequest = getSingleOriginalRequestByName(props.activeWidgetRequests, 'radar-chart');

  const currentState = {...radarChartDefaultSettings, ...props.widget?.data?.settings};
  const align = currentState.legendAlign?.horizontal || WidgetLegendSettingsDefaults.legendAlign?.horizontal;
  const verticalAlign = currentState.legendAlign?.vertical || WidgetLegendSettingsDefaults.legendAlign?.vertical;

  const chartColors = theme.colors.namedColors.chartColorArray;

  const categoryData:CategoryWidgetData | undefined = useMemo(() => {
    const catAPIResponseInfo = getSingleWidgetResponseByName(props.apiResponses, 'radar-chart');
    if (!catAPIResponseInfo) {
      return undefined;
    }
    return MakeCategorySegmentsFromSingleResponse(catAPIResponseInfo, props.queryInfo, theme);
  }, [props.apiResponses, props.queryInfo, props.widget, theme]);

  const label = categoryData?.fieldLabel || "";
  const aspectRatio = (props.contentRef.current.clientWidth ?? 1) / (props.contentRef.current.clientHeight ?? 1);

  const categoryRows = categoryData?.categoryRows?.map((row: CategoryWidgetCategory, index: number) => {
    return {
      subject: row.label,
      percent: row.percent * 100,
      count: row.count,
      color: row.color,
      fullMark: 100,
      query: row.drilldownQuery
    }
  }) || [];

  const displayRows = categoryRows.map((row, index) => {
    return (
      <Radar
        key={index}
        name={row.subject}
        dataKey="count"
        fill={theme.colors.namedColors.uncategorizedLight}
        fillOpacity={0.02}
        dot={currentState.showDots ? (params) => {
          return (
            <circle
              key={params.index}
              r={currentState.dotSize}
              cx={params.cx}
              cy={params.cy}
              fill={chartColors[params.index]}
              stroke={chartColors[params.index]}
              className='recharts-dot recharts-radar-dot' />
          );
        } : false}
      />
    );
  }) || [];

  const FormattedTick = (props: any) => {
    const { x, y, payload } = props;
    return (
      <g transform={`translate(${x},${y})`}>
        <text x={5} y={5} textAnchor="left" fill={theme.colors.namedColors.uncategorized} fontSize={10} fontWeight={"bold"} >
          {payload.value.toLocaleString()}
        </text>
      </g>
    );
  };

  return (
    <>
      {categoryRows.length !== 0 &&
        <WidgetLayout
          header={
            <WidgetTitle
              alignment={currentState.widgetTitleAlignment}
              customTitle={currentState.customWidgetTitle}
              defaultTitle={label}
              settingsTitle={currentState.widgetTitle}
              widgetQueryTitle={apiResponseInfo?.queryDisplayTitle} />
          }
        >
          <>
            {categoryRows.length < 3 ?
              <Box>
                <WidgetAlert severity="warning" alertTitleText="Display Error">
                  {i18n.t("widget-types.radar-chart.alerts.display-error")}
                </WidgetAlert>
              </Box>
              :
              <WidgetLegend
                colors={chartColors}
                data={categoryRows.slice(0, parseInt(currentState.categoryCount ?? 5))}
                horizontalAlign={align}
                layout={currentState.legendLayout}
                show={currentState.legendShow}
                valueGetter={(item) => item.subject}
                verticalAlign={verticalAlign}
              >
                <Tooltip
                  title={(isHoveringChart || isHoveringLabel) ? <RadarChartTooltip data={categoryRows} index={hoveredIndex} /> : ""}
                  open={isHoveringChart || isHoveringLabel}
                  followCursor={true}
                >
                  <Box sx={{
                    height: "100%"
                  }}>
                    <ResponsiveContainer>
                      <RadarChart
                        margin={{top: 20, bottom: 0}}
                        data={categoryRows.slice(0, parseInt(currentState.categoryCount ?? 5))}
                        onMouseMove={(chart) => {
                          if (chart.activeTooltipIndex || chart.activeTooltipIndex === 0) {
                            setIsHoveringChart(true);
                            setHoveredIndex(chart.activeTooltipIndex);
                          } else {
                            setIsHoveringChart(false);
                          }
                        }}
                        onClick={(event) => {
                          setIsHoveringChart(false);
                          if (event.activePayload && event.activeTooltipIndex !== undefined) {
                            if (event.activeTooltipIndex >= 0 && event.activeTooltipIndex < categoryRows.length) {
                              setSelectedValue(categoryRows[event.activeTooltipIndex]);
                            }
                          }
                        }}
                      >
                        <PolarGrid />
                        <PolarAngleAxis
                          dataKey="subject"
                          tick={currentState.showLabel ?
                            (props) => {
                              const { x, y, cx, cy, orientation, radius, stroke, textAnchor, payload } = props;
                              let { value: label } = payload;
                              let labelMaxLength = 4;

                              // Compute labelMaxLength based on aspectRatio
                              if (aspectRatio > 1.2 && aspectRatio < 1.5) {
                                labelMaxLength = 8 * Math.round(aspectRatio);
                              } else if (aspectRatio >= 1.5 && aspectRatio < 1.75) {
                                labelMaxLength = 9 * Math.round(aspectRatio);
                              } else if (aspectRatio >= 1.75) {
                                labelMaxLength = 6 * Math.round(aspectRatio);
                              }

                              if (/[^\u0000-\u00ff]/.test(label)) {
                                labelMaxLength += 4;
                              }

                              // Truncate rules map
                              const truncateRules = [
                                { condition: aspectRatio < 1.5, offset: -1 },
                                { condition: aspectRatio >= 1.5 && aspectRatio < 1.75, offset: -10 },
                                { condition: aspectRatio >= 1.75 && aspectRatio < 2, offset: +10 },
                                { condition: aspectRatio >= 2 && aspectRatio < 2.5, offset: +7 },
                                { condition: aspectRatio >= 2.5 && aspectRatio < 3, offset: 0 },
                                { condition: aspectRatio >= 3, offset: +10 },
                              ];

                              for (const { condition, offset } of truncateRules) {
                                if (label.length > labelMaxLength && condition) {
                                  const newLength = Math.max(0, labelMaxLength + offset);
                                  if (label !== label.slice(0, newLength)) {
                                    label = label.slice(0, newLength) + "…";
                                  }
                                  break;
                                }
                              }

                              return (
                                <text
                                  x={x}
                                  y={y}
                                  cx={cx}
                                  cy={cy}
                                  orientation={orientation}
                                  radius={radius}
                                  stroke={stroke}
                                  textAnchor={textAnchor}
                                  fill={theme.palette.common.black}
                                >
                                  {label}
                                </text>
                              );
                            } : false}
                          onMouseEnter={(data) => {
                            setIsHoveringLabel(true);
                            setHoveredIndex(data.index);
                          }}
                          onMouseLeave={(data) => {
                            setIsHoveringLabel(false);
                          }}
                          onClick={(data) => {
                            setIsHoveringLabel(false);
                            if (data.index !== undefined) {
                              setSelectedValue(categoryRows[data.index]);
                            }
                          }}
                        />
                        <PolarRadiusAxis
                          angle={90}
                          domain={[0, 'dataMax']}
                          tick={<FormattedTick />}
                        />
                        {displayRows}
                      </RadarChart>
                    </ResponsiveContainer>
                  </Box>
                </Tooltip>
                </WidgetLegend>
            }
          </>
        </WidgetLayout>
      }
      {categoryRows.length === 0 &&
        <WidgetAlert type={WidgetErrorType.NO_DATA} />
      }
      {selectedValue &&
        <DashboardQueryDrilldownDialog
          title={selectedValue.subject}
          queryInfo={props.queryInfo}
          onClose={() => setSelectedValue(null)}
          activeQueryIds={apiResponseInfo?.originatingRequest?.activeQueryIds}
          widgetRequestInfo={originalRequest}
          drilldownQuery={new Query({ query: selectedValue.query.query})}
        />
      }
    </>
  );
}

export default withQueryWidget(RadarChartContent, {
  hasSettingsDialog: true,
  hasQueryDialog: true,
});
